import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Hidden,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import noResultsFound from '../../assets/images/noResultFound.svg'
import { totalQuantityVar } from '../../cache/cart'
import AdotelSkeleton from '../../components/AdotelSkeleton'
import { RedirectModal } from '../../components/RedirectModal'
import { BOOKING_DETAILS_QUERY } from '../../graphql/bookingDetail'
import { GET_CART_QUERY } from '../../graphql/cartCrud'
import {
  CONFIRM_PRIMITIVE_RESERVATION,
  MAKE_PRIMITIVE_RESERVATION
} from '../../graphql/cartPrimitive'
import { ACCOMMODATION_IMAGES_QUERY } from '../../graphql/upload'
import { WALLETS_QUERY } from '../../graphql/wallet'
import { CREDITORS_QUERY } from '../../graphql/walletCreditors'
import {
  getCartProviderIdentity,
  getPrimitiveProviderInputCode
} from '../../helpers/cartProviderIdentity'
import { errorToastMessage } from '../../helpers/errorToast'
import { useWindowDimensions } from '../../helpers/pageSize'
import { errorNoCartFound, extractError } from '../../helpers/regex'
import { cleanCartData, getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ConfirmReserveInput } from '../../types/ConfirmReserve'
import { BookingDetail } from '../../types/bookingDetails'
import {
  CartResponse,
  ConfirmPrimitiveReservationInput,
  ConfirmPrimitiveReservation,
  ConfirmRoom,
  MakePrimitiveReservation,
  BillingSummary,
  CartPaymentInfo,
  GrpcInput,
  AccommodationInfo,
  PageStepper,
  DetailedRoomStays,
  Guest,
  PaymentMethod,
  PromotionIntent
} from '../../types/cart'
import { Currency } from '../../types/currency'
import { Image } from '../../types/image'
import { WalletListItem } from '../../types/wallet'
import { CreditorListItem } from '../../types/walletCreditor'
import ChannelBallance from '../BookingHotelSearchResult/components/ChannelBalance'
import StepOne from './StepOne'
import StepThree from './StepThree'
import StepTwo from './StepTwo'
import CustomStepper from './components/CustomStepper'
import ExpireTimeCounter from './components/ExpireTimeCounter'
import FinalBillingSummary from './components/FinalBillingSummary'
import HotelCard from './components/HotelCard'
import PaymentGateWay from './components/PaymentGateWay'
import { parseStoredConfirmPrimitiveInput } from './parseStoredConfirmPrimitiveInput'
import {
  isUsableConfirmPrimitiveOutput,
  isUsableHotelDetails,
  isUsablePrimitiveData,
  parseStoredObject
} from './parseStoredObject'
import { pmsRequestedMethodFor } from './pmsRequestedMethod'
import {
  promotionReservationIntent,
  ValidatedPromotionCode
} from './promotionReservationIntent'
import useStyles from './styles/index.styles'
import { validateConfirmPrimitiveInput } from './validateConfirmPrimitiveInput'

const BookingStepper = () => {
  const classes = useStyles()
  const theme = useTheme()
  const history = useHistory()
  const params = useParams<{
    channelId: string
    code: string
    name: string
    page: PageStepper
    reservationNumber?: string
  }>()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const { width } = useWindowDimensions()
  const [hotelDetails, setHotelDetails] = useState<{
    accommodationInfo: AccommodationInfo
    walletType: string
    provider: string
  }>()
  const cartID = localStorage.getItem('cartID') || ''
  // Default to automatic so the property's auto-applied promotions fire without the
  // operator doing anything, and STAY on when a code is added — the two fields are
  // independent, not alternatives.
  //
  // This comment previously said a typed code "replaces it" because "the two are mutually
  // exclusive in PromotionIntentInput". That was the fourth copy of a claim that is false
  // in three separate ways: the schema declares two independent fields, loyalty's
  // resolveIntent errors only when NEITHER is set, and handleStepUp below has always sent
  // both together. It is also the belief that produced the bug MR !1234 fixed — the
  // advertised list is gated to code_entry promotions while applyAutomatic applies only
  // auto_applied ones, so treating them as exclusive forfeited a discount rather than
  // choosing between two.
  const [promotionIntent, setPromotionIntent] = useState<PromotionIntent>({
    applyAutomatic: true
  })
  const [validatedPromotionCode, setValidatedPromotionCode] =
    useState<ValidatedPromotionCode>()
  const [isTimeExpired, setIsTimeExpired] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [redirectLink, setRedirectLink] = useState('')
  const [redirectTitle, setRedirectTitle] = useState('')
  const [clickedItemHash, setClickedItemHash] = useState<string>('hash')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>()

  const [confirmReserveInput, setConfirmReserveInput] =
    useState<ConfirmReserveInput>({
      paymentMethod: '',
      reservationNumber: ''
    })
  const [expireTime, setExpireTime] = useState('')
  const [billingData, setBillingData] = useState<BillingSummary>({
    roomsPrice: [],
    subTotalPrice: {
      subTotalBeforeDiscount: 0,
      discount: 0,
      additionalDiscount: 0,
      subTotalAfterDiscount: 0
    },
    taxAndFees: 0,
    totalPayable: 0,
    curruncy: 'IRR'
  })
  const [paymentInfo, setPaymentInfo] = useState<CartPaymentInfo>({
    walletID: '',
    walletType: '',
    cashBalance: 0,
    remainingCreditBalance: 0,
    isBilateral: false,
    currency: 'IRR',
    creditorID: ''
  })
  const [grpcInput, setGrpcInput] = useState<GrpcInput>({
    channelCode: '',
    propertyCode: '',
    providerCode: ''
  })
  const [walletType, setWalletType] = useState('')
  const [providerCode, setProviderCode] = useState('')
  const [currency, setCurrency] = useState<Currency>()

  const [confirmPrimitiveOutput, setConfirmPrimitiveOutput] = useState<
    ConfirmPrimitiveReservation | undefined
  >(undefined)

  const [primitiveData, setPrimitiveData] = useState<
    MakePrimitiveReservation | undefined
  >(undefined)

  const [confirmPrimitiveInput, setConfirmPrimitiveInput] =
    useState<ConfirmPrimitiveReservationInput>({
      reservationNumber: '',
      pmsRequestedMethod: pmsRequestedMethodFor(getRoleFromStorage(), 'Sync'),
      booker: {
        firstName: '',
        lastName: '',
        email: '',
        identificationNo: '',
        mobile: '',
        isForeigner: false
      },
      discountPercent: 0,
      confirmRooms: [],
      walletID: '',
      grpcInput: { channelCode: '', propertyCode: '', providerCode: '' },
      cartID: cartID
    })
  const [optionReservationExpiry, setOptionReservationExpiry] = useState(false)

  //CREDITOR_LIST_QUERY
  const [getCreditors, { loading: loadingForCreditors }] = useLazyQuery<{
    creditors: CreditorListItem[]
  }>(CREDITORS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const matchingCreditor = res.creditors.find(
        (creditor) =>
          creditor.active && creditor.entity.displayEntityCode === providerCode
      )

      setConfirmPrimitiveOutput({
        ...confirmPrimitiveOutput!,
        paymentInfo: {
          cashBalance: matchingCreditor?.walletBalance || 0,
          remainingCreditBalance: matchingCreditor?.remainingCreditBalance || 0,
          isBilateral: true,
          walletType: walletType,
          currency: currency,
          walletID: matchingCreditor?.walletID || '',
          creditorID: matchingCreditor?.creditorID || ''
        }
      })
    }
  })

  //WALLET_LIST_QUERY
  const [getWallets, { loading: loadingForWallets, refetch }] = useLazyQuery<{
    wallets: WalletListItem[]
  }>(WALLETS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (walletType === 'ProviderCredit') {
        getCreditors({
          variables: {
            filters: {
              walletID: res.wallets.find((w) => w.active && w.isBilateral)
                ?.walletID,
              sortBy: 'default'
            }
          }
        })
      } else {
        const matchingWallet = res.wallets.find(
          (w) => w.active && !w.isBilateral
        )
        setConfirmPrimitiveOutput({
          ...confirmPrimitiveOutput!,
          paymentInfo: {
            cashBalance: matchingWallet?.walletBalance || 0,
            remainingCreditBalance: matchingWallet?.remainingCreditBalance || 0,
            isBilateral: false,
            walletType: matchingWallet?.walletType || '',
            currency: currency,
            walletID: matchingWallet?.walletID || ''
          }
        })
      }
    }
  })

  //GET_ACCOMMODATION_IMAGE
  const [getAccommodationImages] = useLazyQuery<{
    accommodationImages: Image[]
  }>(ACCOMMODATION_IMAGES_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setHotelDetails({
        accommodationInfo: {
          propertyCode: hotelDetails?.accommodationInfo?.propertyCode ?? '',
          name: hotelDetails?.accommodationInfo?.name ?? '',
          city: hotelDetails?.accommodationInfo?.city ?? '',
          star: hotelDetails?.accommodationInfo?.star ?? 0,
          images: res.accommodationImages.map((image) => image.url)
        },
        provider: hotelDetails?.provider ?? '',
        walletType: hotelDetails?.walletType ?? ''
      })
    }
  })

  //BOOKING_DETAILS_QUERY
  const { loading: loadingForBookingDetails } = useQuery<{
    bookingDetail: BookingDetail
  }>(BOOKING_DETAILS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      reservationNumber: params.reservationNumber
    },
    skip: !params.reservationNumber || params.page !== 'payment',
    onCompleted: (res) => {
      setWalletType(res.bookingDetail.basicDetails.walletType)
      setProviderCode(res.bookingDetail.basicDetails.displayProviderCode)
      setCurrency(res.bookingDetail.billingSummary.curruncy || 'IRR')
      getAccommodationImages({
        variables: {
          propertyCode: res.bookingDetail.basicDetails.propertyCode
        }
      })
      setConfirmPrimitiveOutput({
        billingSummary: {
          totalPayable: res.bookingDetail.billingSummary.finalAmount,
          curruncy: res.bookingDetail.billingSummary.curruncy,
          roomsPrice: res.bookingDetail.billingSummary.roomsPrice,
          subTotalPrice: {
            subTotalBeforeDiscount:
              res.bookingDetail.billingSummary.subTotalBeforeDiscount,
            subTotalAfterDiscount:
              res.bookingDetail.billingSummary.subTotalAfterDiscount,
            discount: res.bookingDetail.billingSummary.discount,
            additionalDiscount:
              res.bookingDetail.billingSummary.additionalDiscount
          },
          taxAndFees: res.bookingDetail.billingSummary.taxAndFees
        },
        paymentInfo: {
          cashBalance: 0,
          remainingCreditBalance: 0,
          walletType: '',
          currency: res.bookingDetail.billingSummary.curruncy,
          walletID: '',
          isBilateral: true,
          creditorID: ''
        },
        reservationInfo: {
          booker: {
            firstName: res.bookingDetail.bookerInfo.firstName,
            lastName: res.bookingDetail.bookerInfo.lastName,
            email: res.bookingDetail.bookerInfo.email,
            identificationNo: res.bookingDetail.bookerInfo.idNumber,
            isForeigner: res.bookingDetail.bookerInfo.isForeigner,
            mobile: res.bookingDetail.bookerInfo.telephone
          },
          providerCode: res.bookingDetail.basicDetails.providerCode,
          displayProviderCode: res.bookingDetail.basicDetails.providerCode,
          expiresAt: res.bookingDetail.basicDetails.expiredAt,
          detailedRoomStays: res.bookingDetail.roomsPriceDetails.map((room) => {
            const matchedRoom = res.bookingDetail.rooms.find(
              (r) =>
                r.roomTypeCode === room.roomTypeCode &&
                r.ratePlanCode === room.ratePlanCode
            )

            return {
              arrival: matchedRoom?.checkIn || '',
              departure: matchedRoom?.checkOut || '',
              boardType: matchedRoom?.boardType || '',
              chargePerDays: room.chargePerDays,
              chargePerRoomStay: room.chargePerRoomStay,
              displayRoomTypeCode: room.roomTypeCode,
              totalChargePerDay: room.totalChargePerDay,
              totalChargePerRoomStay: room.totalChargePerRoomStay,
              discount: res.bookingDetail.billingSummary.discount ?? 0,
              ratePlanCode: room.ratePlanCode,
              displayRatePlanCode: room.ratePlanCode,
              roomTypeName: room.roomTypeName,
              roomTypeCode: room.roomTypeCode,
              ratePlanName: matchedRoom?.ratePlanName || '',
              guests:
                matchedRoom?.profiles.map(
                  (g): Guest => ({
                    name: `${g.firstName} ${g.lastName}`,
                    email: g.email,
                    mobile: g.phone
                  })
                ) ?? [],
              occupancy: matchedRoom?.occupancy ?? {
                adults: 0,
                children: 0,
                total: 0,
                extraBeds: 0,
                maxOccupancy: 0,
                defaultOccupancy: 0
              }
            } satisfies DetailedRoomStays
          }),
          selectedRooms: res.bookingDetail.rooms.map((room) => ({
            roomTypeName: room.roomTypeName,
            ratePlanName: room.ratePlanName,
            occupancy: room.occupancy,
            boardType: room.boardType,
            checkIn: room.checkIn,
            checkOut: room.checkOut
          })),

          totalAmount: {
            payable: res.bookingDetail.billingSummary.finalAmount,
            discount: res.bookingDetail.billingSummary.discount
          },

          paymentMethods: []
        },
        reservationNumber: res.bookingDetail.basicDetails.reservationNumber
      })
      setHotelDetails({
        accommodationInfo: {
          ...(hotelDetails?.accommodationInfo ?? {}),
          propertyCode: res.bookingDetail.basicDetails.propertyCode,
          name: res.bookingDetail.basicDetails.accommodationName,
          star: 0,
          city:
            lang === 'fa'
              ? res.bookingDetail.basicDetails.propertyAddressFA
              : res.bookingDetail.basicDetails.propertyAddress
        },
        provider: res.bookingDetail.basicDetails.providerName,
        walletType: res.bookingDetail.basicDetails.walletType
      })
    },
    onError: (error) => {
      toast.error(errorToastMessage(error))
    }
  })

  useEffect(() => {
    if (confirmPrimitiveOutput && !params.reservationNumber) {
      setWalletType(confirmPrimitiveOutput.paymentInfo.walletType)
      setCurrency(confirmPrimitiveOutput.billingSummary.curruncy)
      setProviderCode(
        confirmPrimitiveOutput.reservationInfo.displayProviderCode
      )
    }
  }, [confirmPrimitiveOutput])

  useEffect(() => {
    if (walletType && currency && providerCode) {
      getWallets({
        variables: {
          filters: {
            entity: {
              entityType: 'Channel',
              entityID: params.channelId
            },
            active: true,
            currency: currency,
            sortBy: 'default'
          }
        }
      })
    }
  }, [walletType, currency, providerCode])

  useEffect(() => {
    const confirmPrimitiveInputLocalStorage = localStorage.getItem(
      'confirmPrimitiveInput'
    )
    const primitiveDataLocalStorage = localStorage.getItem('primitiveData')
    const confirmPrimitiveReservationStateLocalStorage = localStorage.getItem(
      'confirmPrimitiveOutput'
    )
    const hotelDetailsLocalStorage = localStorage.getItem('hotelDetails')

    if (confirmPrimitiveInputLocalStorage) {
      // Only adopt a persisted value that still has the shape this build expects.
      //
      // This state is written to localStorage and read back on the next visit, so a
      // browser can hold an object written by an older build. It was replacing the
      // initialised state wholesale, and the reserve step then rendered against an
      // object with no confirmRooms -- confirmPrimitiveInput.confirmRooms.slice(...)
      // threw and the screen went blank with nothing on it to explain why.
      //
      // That TypeError also landed in Apollo's promise chain, so onError ran with a
      // TypeError instead of an ApolloError and crashed again on graphQLErrors[0].
      // The second error looked like the cause and was not; see the guard there.
      const restored = parseStoredConfirmPrimitiveInput(
        confirmPrimitiveInputLocalStorage
      )
      if (restored) {
        setConfirmPrimitiveInput({
          ...restored,
          pmsRequestedMethod: pmsRequestedMethodFor(
            getRoleFromStorage(),
            restored.pmsRequestedMethod
          )
        })
      } else {
        // A value we cannot use is worse than none: leaving it means every later
        // visit reads it again and breaks the same way.
        localStorage.removeItem('confirmPrimitiveInput')
      }
    }

    // The three below were left on a bare JSON.parse when confirmPrimitiveInput above was
    // guarded, and primitiveData then blanked the reserve step the same way: its stored
    // billingSummary had no roomsPrice, and FinalBillingSummary slices that during render.
    // Guarding one of four siblings fixed one of four routes to the same blank screen.
    if (primitiveDataLocalStorage) {
      const restored = parseStoredObject<MakePrimitiveReservation>(
        primitiveDataLocalStorage,
        isUsablePrimitiveData
      )
      if (restored) {
        setPrimitiveData(restored)
      } else {
        localStorage.removeItem('primitiveData')
      }
    }

    if (
      confirmPrimitiveReservationStateLocalStorage &&
      !params.reservationNumber
    ) {
      const restored = parseStoredObject<ConfirmPrimitiveReservation>(
        confirmPrimitiveReservationStateLocalStorage,
        isUsableConfirmPrimitiveOutput
      )
      if (restored) {
        setConfirmPrimitiveOutput(restored)
      } else {
        localStorage.removeItem('confirmPrimitiveOutput')
      }
    }

    if (hotelDetailsLocalStorage && !params.reservationNumber) {
      const restored = parseStoredObject<{
        accommodationInfo: AccommodationInfo
        walletType: string
        provider: string
      }>(hotelDetailsLocalStorage, isUsableHotelDetails)
      if (restored) {
        setHotelDetails(restored)
      } else {
        localStorage.removeItem('hotelDetails')
      }
    }
  }, [])

  //GET_CART_QUERY
  const {
    data: cartData,
    loading: loadingForGetCart,
    refetch: refetchCart
  } = useQuery<{
    cartResponse: CartResponse
  }>(GET_CART_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !cartID || params.page !== 'cart',
    fetchPolicy: 'network-only',
    variables: { cartID: cartID },
    onCompleted: (res) => {
      const elemens = res.cartResponse.cart.items || []
      const totalQuantity = elemens.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
      )
      totalQuantityVar(totalQuantity)
      localStorage.setItem('totalQuantity', totalQuantity.toString())
      if (res.cartResponse.cart.items.length === 0) {
        setRedirectLink(
          `/${lang}/book/${params.channelId}/${params.code}/${params.name}`
        )
        setRedirectTitle('Cart is empty')
        setShowModal(true)
      } else {
        setHotelDetails({
          accommodationInfo: res.cartResponse.cart.accommodationInfo,
          walletType: res.cartResponse.cart.paymentInfo.walletType,
          provider: getCartProviderIdentity(res.cartResponse.cart.items[0]).name
        })
      }
    },
    onError: (err) => {
      // graphQLErrors is empty for a network failure, and absent entirely when what
      // arrives is not an ApolloError at all -- a render that throws downstream of a
      // successful mutation reaches here as a plain TypeError. Indexing [0] blindly
      // turned both cases into a second, misleading crash that hid the first.
      const firstGraphQLError = err?.graphQLErrors?.[0]
      if (
        firstGraphQLError &&
        extractError(firstGraphQLError.extensions) === errorNoCartFound
      ) {
        setShowModal(true)
        setRedirectTitle('Your cart has expired.')
        setRedirectLink(
          `/${lang}/book/${params.channelId}/${params.code}/${params.name}`
        )
        cleanCartData()
        localStorage.removeItem('confirmPrimitiveInput')
        localStorage.removeItem('confirmPrimitiveOutput')
        localStorage.removeItem('hotelDetails')
        localStorage.removeItem('primitiveData')
      } else {
        // Was a bare "Something went wrong", which discarded whatever the server said while
        // the three sibling handlers in this file showed it. The helper keeps the fallback for
        // the cases that genuinely carry nothing useful -- a network failure, or a render that
        // threw downstream of a succeeded mutation.
        toast.error(errorToastMessage(err))
      }
    }
  })

  // A promotion evaluation prices the current cart, not merely its ID. Keep the complete
  // server snapshot in the validation identity so any refetched cart change invalidates
  // the accepted code at the reservation boundary.
  const promotionCartKey = JSON.stringify(cartData?.cartResponse.cart ?? null)

  //MAKE_PRIMITIVE_RESERVATION
  const [makePrimitive, { loading: loadingForMakePrimitive }] = useMutation<{
    makePrimitiveReservation: MakePrimitiveReservation
  }>(MAKE_PRIMITIVE_RESERVATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      setPrimitiveData(res.makePrimitiveReservation)
      const rooms: ConfirmRoom[] = res.makePrimitiveReservation.roomStays.map(
        (r) => {
          return {
            crsNumber: r.crsNumber,
            description: '',
            max: r.occupancy.adults + r.occupancy.extraBeds,
            profiles: [
              {
                firstName: '',
                lastName: '',
                email: '',
                identificationNo: '',
                mobile: '',
                isForeigner: false
              }
            ]
          }
        }
      )

      setConfirmPrimitiveInput((prev) => ({
        ...prev,
        reservationNumber: res.makePrimitiveReservation.reservationNumber,
        walletID: res.makePrimitiveReservation.paymentInfo.walletID,
        creditorID: res.makePrimitiveReservation.paymentInfo.creditorID,
        confirmRooms: rooms,
        grpcInput: {
          channelCode: params.code,
          propertyCode: res.makePrimitiveReservation.propertyCode,
          providerCode: getPrimitiveProviderInputCode(
            res.makePrimitiveReservation
          )
        }
      }))
      history.replace(
        `/${lang}/book/${params.channelId}/${params.code}/${params.name}/bookstepper/form`
      )
    },
    onError: (err) => {
      // This is the handler the blank-screen crash actually reached. A render that throws
      // downstream of a SUCCEEDED reservation arrives here as a plain TypeError with no
      // graphQLErrors at all, and indexing [0] turned that into a second crash that hid the
      // first. graphQLErrors is also empty for an ordinary network failure.
      const firstGraphQLError = err?.graphQLErrors?.[0]
      if (
        firstGraphQLError &&
        extractError(firstGraphQLError.extensions) === errorNoCartFound
      ) {
        setShowModal(true)
        setRedirectLink(
          `/${lang}/book/${params.channelId}/${params.code}/${params.name}`
        )
        setRedirectTitle('Your cart has expired.')
        cleanCartData()
        localStorage.removeItem('confirmPrimitiveInput')
        localStorage.removeItem('confirmPrimitiveOutput')
        localStorage.removeItem('hotelDetails')
        localStorage.removeItem('primitiveData')
      } else {
        toast.error(errorToastMessage(err))
      }
    }
  })

  //CONFIRM_PRIMITIVE_RESERVATION
  const [
    confirmPrimitiveReservation,
    { loading: loadingForConfirmPrimitiveData }
  ] = useMutation<{ confirmPrimitiveReservation: ConfirmPrimitiveReservation }>(
    CONFIRM_PRIMITIVE_RESERVATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        localStorage.removeItem('confirmPrimitiveInput')
        setConfirmPrimitiveOutput(res.confirmPrimitiveReservation)
        history.replace(
          `/${lang}/book/${params.channelId}/${params.code}/${params.name}/bookstepper/payment`
        )
        cleanCartData()
      },
      onError: (err) => {
        toast.error(errorToastMessage(err))
      }
    }
  )

  useEffect(() => {
    if (confirmPrimitiveInput) {
      localStorage.setItem(
        'confirmPrimitiveInput',
        JSON.stringify(confirmPrimitiveInput)
      )
    }

    if (primitiveData) {
      localStorage.setItem('primitiveData', JSON.stringify(primitiveData))
    }

    if (confirmPrimitiveOutput && !params.reservationNumber) {
      localStorage.setItem(
        'confirmPrimitiveOutput',
        JSON.stringify(confirmPrimitiveOutput)
      )
    }

    if (hotelDetails) {
      localStorage.setItem('hotelDetails', JSON.stringify(hotelDetails))
    }
  }, [
    confirmPrimitiveInput,
    primitiveData,
    confirmPrimitiveOutput,
    hotelDetails
  ])

  const renderStep = () => {
    switch (params.page) {
      case 'cart':
        return (
          <StepOne
            cart={cartData?.cartResponse.cart}
            cartID={cartID}
            refetchCart={refetchCart}
            loadingForGetCart={loadingForGetCart}
            setShowModal={setShowModal}
            setRedirectLink={setRedirectLink}
            clickedItemHash={clickedItemHash}
            setClickedItemHash={setClickedItemHash}
            setRedirectTitle={setRedirectTitle}
            setIsTimeExpired={setIsTimeExpired}
            promotionIntent={promotionIntent}
            setPromotionIntent={setPromotionIntent}
            promotionCartKey={promotionCartKey}
            onValidatedPromotionCodeChange={setValidatedPromotionCode}
          />
        )
      case 'form':
        return (
          <StepTwo
            primitiveData={primitiveData}
            confirmPrimitiveInput={confirmPrimitiveInput}
            setConfirmPrimitiveInput={setConfirmPrimitiveInput}
            setShowModal={setShowModal}
            setRedirectTitle={setRedirectTitle}
            setRedirectLink={setRedirectLink}
            isTimeExpired={isTimeExpired}
            setIsTimeExpired={setIsTimeExpired}
            optionReservationExpiry={optionReservationExpiry}
            setOptionReservationExpiry={setOptionReservationExpiry}
          />
        )
      case 'payment':
        return (
          <StepThree
            confirmPrimitiveData={confirmPrimitiveOutput}
            isTimeExpired={isTimeExpired}
            setRedirectLink={setRedirectLink}
            setShowModal={setShowModal}
            setRedirectTitle={setRedirectTitle}
          />
        )
      default:
        return
    }
  }

  const handleStepUp = () => {
    if (params.page === 'cart') {
      makePrimitive({
        variables: {
          input: {
            cartID: cartID,
            grpcInput: grpcInput,
            // The field is a draft until the evaluation service accepts it. Re-check the
            // draft against the accepted value at this final boundary as well, so editing
            // and immediately continuing cannot submit the previous validation.
            promotionIntent: promotionReservationIntent(
              promotionIntent,
              promotionCartKey,
              validatedPromotionCode
            )
          }
        }
      })
    } else if (params.page === 'form') {
      confirmPrimitiveReservation({
        variables: {
          input: confirmPrimitiveInput
        }
      })
    }
  }

  const handleStepDown = () => {
    if (params.page === 'form') {
      history.replace(
        `/${lang}/book/${params.channelId}/${params.code}/${params.name}/bookstepper/cart`
      )
    } else if (params.page === 'cart') {
      history.goBack()
    }
  }

  useEffect(() => {
    if (paymentMethod) {
      setConfirmReserveInput((prev) => ({
        ...prev,
        paymentMethod: paymentMethod.toString()
      }))
    }
  }, [paymentMethod])

  useEffect(() => {
    if (
      params.page === 'cart' &&
      cartData &&
      cartData.cartResponse.cart.items.length > 0
    ) {
      setHotelDetails({
        accommodationInfo: cartData.cartResponse.cart.accommodationInfo,
        walletType: cartData.cartResponse.cart.paymentInfo.walletType,
        provider: getCartProviderIdentity(cartData.cartResponse.cart.items[0])
          .name
      })
      setBillingData(cartData.cartResponse.billingSummary)
      setPaymentInfo(cartData.cartResponse.cart.paymentInfo)
      setGrpcInput({
        channelCode: cartData.cartResponse.cart.channelCode,
        propertyCode: cartData.cartResponse.cart.accommodationInfo.propertyCode,
        providerCode: getCartProviderIdentity(
          cartData.cartResponse.cart.items[0]
        ).inputCode
      })
    }
    if (params.page === 'form' && primitiveData) {
      setBillingData(primitiveData.billingSummary)
      setPaymentInfo(primitiveData.paymentInfo)
      setExpireTime(primitiveData?.expiresAt.replace(' ', 'T') || '')
    }
    if (params.page === 'payment' && confirmPrimitiveOutput) {
      setBillingData(confirmPrimitiveOutput.billingSummary)
      setPaymentInfo(confirmPrimitiveOutput.paymentInfo)
      setExpireTime(
        confirmPrimitiveOutput.reservationInfo.expiresAt.replace(' ', 'T') || ''
      )
    }
  }, [params.page, cartData, primitiveData, confirmPrimitiveOutput])

  const calcStep = (p: string) => {
    switch (p) {
      case 'cart':
        return 0

      case 'form':
        return 1

      case 'payment':
        return 2

      default:
        return 0
    }
  }

  return (
    <>
      <div className={classes.stickyDiv}>
        <div className={classes.stepContainer}>
          <CustomStepper activeStep={calcStep(params.page)} />
        </div>
      </div>
      <div
        className={[
          classes.root,
          isSmall
            ? classes.rootSmall
            : width <= 1280
            ? classes.root1280
            : width < 1440
            ? classes.root1440
            : width <= 1680
            ? classes.root1680
            : ''
        ].join(' ')}>
        {cartData || primitiveData || confirmPrimitiveOutput ? (
          <>
            <div className={classes.left}>
              <Hidden smUp>
                {params.page !== 'cart' && (
                  <ExpireTimeCounter
                    expiryTime={expireTime}
                    setIsTimeExpired={setIsTimeExpired}
                  />
                )}
              </Hidden>
              <HotelCard hotelDetails={hotelDetails} />
              {renderStep()}
            </div>
            <div className={classes.Right}>
              {params.page !== 'cart' && (
                <Hidden smDown>
                  <ExpireTimeCounter
                    expiryTime={expireTime}
                    setIsTimeExpired={setIsTimeExpired}
                  />
                </Hidden>
              )}
              <Hidden smDown>
                <ChannelBallance
                  channelName={params.name || ''}
                  paymentInfo={paymentInfo}
                />
              </Hidden>
              <FinalBillingSummary
                billingData={billingData}
                step={calcStep(params.page) + 1}
                showTaxText={hotelDetails?.accommodationInfo?.star! >= 3}
                promotionApplication={primitiveData?.promotionApplication}
                roomStays={primitiveData?.roomStays}
                requestedPromotionCode={promotionIntent.code}
              />
              {params.page === 'payment' && !isSmall && (
                <PaymentGateWay
                  totalAmount={
                    confirmPrimitiveOutput?.reservationInfo.totalAmount
                  }
                  paymentInfo={paymentInfo}
                  confirmReserveInput={confirmReserveInput}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  isTimeExpired={isTimeExpired}
                  reservationNumber={
                    confirmPrimitiveOutput?.reservationNumber || ''
                  }
                  refetch={refetch}
                />
              )}
              {params.page !== 'payment' && !isSmall && (
                <div className={classes.buttons}>
                  <Button
                    variant="outlined"
                    className={classes.btn}
                    onClick={handleStepDown}>
                    <Typography variant="h4" className={classes.cancelText}>
                      {params.page == 'cart'
                        ? getTranslate('Back To Booking')
                        : getTranslate('Back To Step 1')}
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.btn}
                    disabled={
                      (params.page === 'form' &&
                        !validateConfirmPrimitiveInput(
                          confirmPrimitiveInput,
                          optionReservationExpiry
                        )) ||
                      isTimeExpired
                    }
                    onClick={handleStepUp}>
                    <Typography variant="h4" className={classes.continueText}>
                      {getTranslate('Continue')}
                    </Typography>
                  </Button>
                </div>
              )}
              {params.page === 'payment' && isSmall && (
                <PaymentGateWay
                  totalAmount={
                    confirmPrimitiveOutput?.reservationInfo.totalAmount
                  }
                  paymentInfo={paymentInfo}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  reservationNumber={
                    confirmPrimitiveOutput?.reservationNumber || ''
                  }
                  isTimeExpired={isTimeExpired}
                  confirmReserveInput={confirmReserveInput}
                  refetch={refetch}
                />
              )}
              {params.page !== 'payment' && isSmall && (
                <div className={classes.buttons}>
                  <Button
                    variant="outlined"
                    className={classes.btn}
                    onClick={handleStepDown}>
                    <Typography variant="h5" className={classes.cancelText}>
                      {params.page == 'cart'
                        ? getTranslate('Back To Booking')
                        : getTranslate('Back To Step 1')}
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.btn}
                    disabled={
                      (params.page === 'form' &&
                        !validateConfirmPrimitiveInput(
                          confirmPrimitiveInput,
                          optionReservationExpiry
                        )) ||
                      isTimeExpired
                    }
                    onClick={handleStepUp}>
                    <Typography variant="h4" className={classes.continueText}>
                      {getTranslate('Continue')}
                    </Typography>
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          !loadingForGetCart &&
          !loadingForConfirmPrimitiveData &&
          !loadingForMakePrimitive &&
          loadingForBookingDetails &&
          loadingForCreditors &&
          loadingForWallets && (
            <div className={classes.noResultFound}>
              <img src={noResultsFound} className={classes.img} />
              <Typography variant="h5" className={classes.noResults}>
                {getTranslate('No result Found')}
              </Typography>
            </div>
          )
        )}
      </div>
      <AdotelSkeleton
        open={
          (clickedItemHash === 'hash' &&
            (loadingForGetCart ||
              loadingForMakePrimitive ||
              loadingForConfirmPrimitiveData ||
              loadingForBookingDetails)) ||
          loadingForConfirmPrimitiveData ||
          loadingForMakePrimitive ||
          loadingForBookingDetails ||
          loadingForWallets ||
          loadingForCreditors
        }
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
      <RedirectModal
        isOpen={showModal}
        title={redirectTitle}
        onClose={() => {
          setShowModal(false)
        }}
        onClick={() => {
          history.push(redirectLink)
        }}
      />
    </>
  )
}

export default BookingStepper
